/**
 * PermissionsProvider + usePermissions
 *
 * Permix-shaped ergonomics over the manifest. The provider is fed the user's
 * authorities once (from application context, where the user is constructed)
 * and memoizes a Permissions instance. Components then read access without
 * ceremony:
 *
 *   const { canEdit } = usePermissions('user')   // bound to one entry
 *   if (canEdit()) { ... }
 *
 *   const { can, canRead } = usePermissions()     // unbound; pass the key
 *   canRead('organization')
 *
 * CRUD verbs (canRead/canCreate/canEdit/canDelete/canWrite) map onto the
 * manifest's read/write authority tiers (see permissions.ts Action).
 */
import React, { createContext, useContext, useMemo } from 'react'
import {
  createPermissions,
  Permissions,
  ManifestKey,
  Action,
} from './permissions'

const PermissionsContext = createContext<Permissions | null>(null)

export const PermissionsProvider: React.FC<{
  authorities: ReadonlyArray<string> | undefined
  children: React.ReactNode
}> = ({ authorities, children }) => {
  const value = useMemo(
    () => createPermissions(authorities ?? []),
    [authorities]
  )
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

/** The raw Permissions instance — for non-hook consumers (e.g. resolveNavigation). */
export function usePermissionsInstance(): Permissions {
  const ctx = useContext(PermissionsContext)
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return ctx
}

/** Unbound API — every verb takes the manifest key. */
export interface UnboundPermissions {
  can(key: ManifestKey, action?: Action): boolean
  cannot(key: ManifestKey, action?: Action): boolean
  missing(key: ManifestKey, action?: Action): string[]
  canRead(key: ManifestKey): boolean
  canCreate(key: ManifestKey): boolean
  canEdit(key: ManifestKey): boolean
  canDelete(key: ManifestKey): boolean
  canWrite(key: ManifestKey): boolean
}

/** Bound API — verbs are zero-arg, pinned to the key passed to usePermissions. */
export interface BoundPermissions {
  can(action?: Action): boolean
  cannot(action?: Action): boolean
  missing(action?: Action): string[]
  canRead(): boolean
  canCreate(): boolean
  canEdit(): boolean
  canDelete(): boolean
  canWrite(): boolean
}

export function usePermissions(): UnboundPermissions
export function usePermissions(key: ManifestKey): BoundPermissions
export function usePermissions(
  key?: ManifestKey
): UnboundPermissions | BoundPermissions {
  const perms = usePermissionsInstance()

  return useMemo(() => {
    if (key !== undefined) {
      const bound: BoundPermissions = {
        can: (action = 'read') => perms.can(key, action),
        cannot: (action = 'read') => !perms.can(key, action),
        missing: (action = 'read') => perms.missing(key, action),
        canRead: () => perms.can(key, 'read'),
        canCreate: () => perms.can(key, 'create'),
        canEdit: () => perms.can(key, 'edit'),
        canDelete: () => perms.can(key, 'delete'),
        canWrite: () => perms.can(key, 'write'),
      }
      return bound
    }

    const unbound: UnboundPermissions = {
      can: (k, action = 'read') => perms.can(k, action),
      cannot: (k, action = 'read') => !perms.can(k, action),
      missing: (k, action = 'read') => perms.missing(k, action),
      canRead: k => perms.can(k, 'read'),
      canCreate: k => perms.can(k, 'create'),
      canEdit: k => perms.can(k, 'edit'),
      canDelete: k => perms.can(k, 'delete'),
      canWrite: k => perms.can(k, 'write'),
    }
    return unbound
  }, [perms, key])
}
